import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, map, BehaviorSubject, tap, catchError, of, forkJoin, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Timesheet } from '../../objects/timesheet';
import { TimesheetSheetsService } from '../google-sheets/timesheet-sheets.service';
import { PartyService } from './party.service';

/** One approval-element on a submission (sequential approver record). */
export interface TimesheetApproval {
  approver?: string;
  approvedAt?: string;
  name?: string;
}

/**
 * One row in the assigned-submissions work-list. Mirrors the wire shape returned
 * by `GET /submissions/assigned/{userEmail}`. The backend buckets server-side;
 * the frontend just renders the three arrays.
 */
export interface AssignedSubmissionRow {
  submissionId: string;
  contractId: string;
  specId: string;
  reportingParty?: string;
  reportingPartyName?: string;
  periodStart?: string;
  periodEnd?: string;
  dateOfRecord?: string;
  frequency?: string;
  status?: string;
  totalHours?: number;
  totalAmount?: number | null;   // null until approved
  approvals?: TimesheetApproval[];
  currentAssignee?: string;
  apVendorNum?: string;
  updatedAt?: string;
  // Check number captured by the payment approver at Mark-Paid time and
  // persisted with the submission (see markPaid). NOTE: in practice this is
  // always null on assigned-queue rows — paid is terminal and drops off the
  // byActiveStatus index this endpoint reads, so a paid submission never
  // appears here. To DISPLAY a saved check number, read the full submission
  // via get-period / by-contract / list-submissions (the detail/spec reads),
  // not this queue. Kept on the row only for wire-shape completeness.
  checkNumber?: string;
  // Pulled from the spec for card display (see enrichFromSpec). The reporting
  // party's name and the spec's timesheet type (employeeType).
  party?: string;
  timesheetType?: string;
}

/** Server-bucketed response from the assigned-submissions endpoint. */
export interface AssignedSubmissions {
  userEmail: string;
  /** Backend hit its row cap (2000) and kept the oldest — the list is NOT complete. */
  truncated?: boolean;
  counts: { forReview: number; forApproval: number; forPayment: number };
  forReview: AssignedSubmissionRow[];
  forApproval: AssignedSubmissionRow[];
  forPayment: AssignedSubmissionRow[];
}

@Injectable({
  providedIn: 'root'
})
export class TimesheetsService {
  private http = inject(HttpClient);
  private timesheetSheetsService = inject(TimesheetSheetsService);
  private partyService = inject(PartyService);

  // Cache for specs
  private allSpecsSubject = new BehaviorSubject<Timesheet[]>([]);
  public allSpecs$ = this.allSpecsSubject.asObservable();

  // ── Spec CRUD ──────────────────────────────────────────

  getAllTimesheetSpecs(): Observable<Timesheet[]> {
    const url = (environment as any).URL_TIMESHEET_SPECS_LIST;
    return this.http.post<any>(url, {}).pipe(
      map(response => response?.specs || response || []),
      tap(specs => this.allSpecsSubject.next(specs)),
      catchError(err => {
        console.error('[TimesheetsService] Failed to load specs:', err);
        return of([]);
      })
    );
  }

  getTimesheetSpec(specId: string): Observable<Timesheet> {
    const url = (environment as any).URL_TIMESHEET_SPECS_GET + specId;
    return this.http.get<any>(url).pipe(
      map(response => response?.spec || response)
    );
  }

  getTimesheetSpecByContract(contractId: string): Observable<Timesheet | null> {
    const url = (environment as any).URL_TIMESHEET_SPECS_BY_CONTRACT + contractId;
    return this.http.get<any>(url).pipe(
      map(response => response?.spec || response || null),
      catchError(() => of(null))
    );
  }

  getTimesheetSpecsByUser(userId: string): Observable<Timesheet[]> {
    const url = (environment as any).URL_TIMESHEET_SPECS_BY_USER + userId;
    return this.http.get<any>(url).pipe(
      map(response => response?.specs || response || [])
    );
  }

  /**
   * Does this org have an in-app accounting stage? Derived from the same
   * `timesheetPaymentStage` env flag that gates the four UI surfaces, so the
   * spec RECORD and the UI can't disagree.
   *
   * Why this has to be sent: the backend defaults `hasAccountingStage` to TRUE
   * and nothing was ever sending it, so every cbhi spec was written with an
   * accounting stage plus an accountant — even though cbhi pays through NetSuite
   * and hides the whole payment stage. On approval the engine then assigned the
   * submission to that accountant and kept the active sentinel, so an approved
   * timesheet sat on their My Work list forever with no action available to
   * clear it. With the flag false, `approved` is terminal: no assignee, no
   * sentinel, and the row leaves My Work.
   *
   * Interim by design. The durable fix is a per-org config on the backend so an
   * org's payment model doesn't depend on what a client happens to send — see
   * docs/backend-prompt-2026-09-04-timesheet-accounting-stage.md.
   */
  private readonly hasAccountingStage = (environment as any).timesheetPaymentStage !== false;

  writeTimesheetSpec(spec: Partial<Timesheet>): Observable<any> {
    const url = (environment as any).URL_TIMESHEET_SPECS_WRITE;
    return this.enrichVendorNames(spec).pipe(
      switchMap(enriched => this.http.post(url, {
        ...enriched,
        hasAccountingStage: this.hasAccountingStage
      }))
    );
  }

  /**
   * Denormalize `reportingPartyName` and `reportingAgencyName` from the party
   * cache so mobile can render the spec card without a vendor-service fan-out.
   * Mirrors the userObject pattern: the name is captured at every save and
   * vendor renames propagate on next save.
   *
   * Only enriches when the corresponding vendorNum field is present in the
   * payload — partial updates (e.g., AI suggestion writes that send `{specId,
   * primaryHourlyRate}`) pass through untouched. Cache misses leave the name
   * field unset rather than nuking whatever's already on the backend record.
   */
  private enrichVendorNames(spec: Partial<Timesheet>): Observable<Partial<Timesheet>> {
    const partyNum = spec.reporting_party?.trim();
    const agencyNum = spec.reporting_agency?.trim();
    if (!partyNum && !agencyNum) return of(spec);

    return forkJoin({
      party: partyNum ? this.partyService.getPartyObject(partyNum) : of(null),
      agency: agencyNum ? this.partyService.getPartyObject(agencyNum) : of(null)
    }).pipe(
      map(({ party, agency }) => {
        const enriched: Partial<Timesheet> = { ...spec };
        if (party?.name) enriched.reportingPartyName = party.name;
        if (agency?.name) enriched.reportingAgencyName = agency.name;
        return enriched;
      })
    );
  }

  replaceTimesheetSpec(specId: string, newSpec: Partial<Timesheet>): Observable<any> {
    const url = (environment as any).URL_TIMESHEET_SPECS_REPLACE + specId + '/replace';
    // Same stamp as writeTimesheetSpec — a replacement spec must not silently
    // reintroduce the accounting stage this org doesn't have.
    return this.http.post(url, {
      newSpec: { ...newSpec, hasAccountingStage: this.hasAccountingStage }
    });
  }

  getAllReplacedTimesheetSpecs(): Observable<Timesheet[]> {
    const url = (environment as any).URL_TIMESHEET_SPECS_REPLACED;
    return this.http.get<any>(url).pipe(
      map(response => response?.specs || response || [])
    );
  }

  // ── Financial / Versioning ─────────────────────────────

  getTimesheetSpecFinancials(specId: string, date?: string): Observable<any> {
    let url = (environment as any).URL_TIMESHEET_SPECS_FINANCIALS + specId + '/financials';
    if (date) url += '?date=' + date;
    return this.http.get<any>(url);
  }

  // ── Submissions / Ledger ───────────────────────────────

  /**
   * The logged-in user's assigned timesheet submissions, server-bucketed into
   * forReview / forApproval / forPayment. Powers the My Work → Provider Timesheets
   * approval queue. The backend decides which bucket each submission belongs in
   * (status + the caller's role on the spec); the frontend just renders them.
   *
   * Fails soft to empty buckets so the queue renders an empty state rather than
   * crashing if the endpoint is unreachable.
   */
  getAssignedSubmissions(userEmail: string): Observable<AssignedSubmissions> {
    const empty: AssignedSubmissions = {
      userEmail,
      counts: { forReview: 0, forApproval: 0, forPayment: 0 },
      forReview: [], forApproval: [], forPayment: []
    };
    const base = (environment as any).URL_TIMESHEET_SUBMISSIONS_ASSIGNED;
    if (!base || !userEmail) return of(empty);
    const url = base + encodeURIComponent(userEmail);
    return this.http.get<any>(url).pipe(
      map(res => this.normalizeAssigned(res, userEmail)),
      switchMap(assigned => this.enrichReportingPartyNames(assigned)),
      catchError(err => {
        console.error('[TimesheetsService] getAssignedSubmissions failed:', err?.status, err?.message);
        return of(empty);
      })
    );
  }

  /**
   * System Administrator ONLY: every timesheet submission still awaiting action,
   * across all HCPs and periods — the admin counterpart to getAssignedSubmissions.
   * Same bucketed envelope and row shape, so the queue renders it identically.
   *
   * The backend gates to role 'System Administrator' (403 otherwise) and reads the
   * same sparse activeStatus index the assigned endpoint reads, minus the
   * "is the caller an assignee" filter. Mirrors the timecards
   * `/admin/submissions/active` route.
   *
   * Fails soft to empty buckets — a 403 (not a system admin) or a 404 (endpoint not
   * deployed yet) shows an empty queue rather than an error, exactly like the
   * timecards admin call.
   */
  getActiveAdminSubmissions(callerEmail = ''): Observable<AssignedSubmissions> {
    const empty: AssignedSubmissions = {
      userEmail: callerEmail,
      counts: { forReview: 0, forApproval: 0, forPayment: 0 },
      forReview: [], forApproval: [], forPayment: []
    };
    const url = (environment as any).URL_TIMESHEET_ADMIN_SUBMISSIONS_ACTIVE;
    if (!url) return of(empty);
    return this.http.get<any>(url).pipe(
      map(res => this.normalizeAssigned(res, res?.userEmail ?? callerEmail)),
      switchMap(assigned => this.enrichReportingPartyNames(assigned)),
      catchError(err => {
        console.error('[TimesheetsService] getActiveAdminSubmissions failed:', err?.status, err?.message);
        return of(empty);
      })
    );
  }

  /**
   * Enrich each work-list row from its spec — the spec holds everything we need
   * for both spec flavors. We stamp three things the backend row doesn't reliably
   * carry, all for card display:
   *   - reportingPartyName : the display label (the backend's `reportingParty` is
   *     blank for User-flavored specs and for older Vendor specs saved before the
   *     name denormalization, so the queue rendered "(unknown party)").
   *   - party              : the reporting party's name (spec view "Party").
   *   - timesheetType      : the spec's `employeeType` (spec view "Timesheet Type").
   *
   * We fetch each distinct spec in the (small) work list. Best-effort: a spec
   * fetch failure leaves that row as-is rather than blocking the queue.
   */
  private enrichReportingPartyNames(assigned: AssignedSubmissions): Observable<AssignedSubmissions> {
    const rows = [...assigned.forReview, ...assigned.forApproval, ...assigned.forPayment];
    const specIds = [...new Set(rows.map(r => r.specId).filter(Boolean))] as string[];
    if (!specIds.length) return of(assigned);

    return forkJoin(
      specIds.map(id =>
        this.getTimesheetSpec(id).pipe(
          map(spec => ({ id, spec })),
          catchError(() => of({ id, spec: null as any }))
        )
      )
    ).pipe(
      map(results => {
        const bySpec = new Map<string, { name: string; party: string; timesheetType: string }>();
        for (const { id, spec } of results) {
          if (!spec) continue;
          bySpec.set(id, {
            name: this.deriveSpecDisplayName(spec),
            party: this.deriveSpecPartyName(spec),
            timesheetType: (spec.employeeType ?? '').toString().trim(),
          });
        }
        const fill = (rows: AssignedSubmissionRow[]): AssignedSubmissionRow[] =>
          rows.map(row => {
            const info = row.specId ? bySpec.get(row.specId) : undefined;
            if (!info) return row;
            const next = { ...row };
            if (!(next.reportingPartyName?.trim() || next.reportingParty?.trim()) && info.name) {
              next.reportingPartyName = info.name;
            }
            if (info.party) next.party = info.party;
            if (info.timesheetType) next.timesheetType = info.timesheetType;
            return next;
          });
        return {
          ...assigned,
          forReview: fill(assigned.forReview),
          forApproval: fill(assigned.forApproval),
          forPayment: fill(assigned.forPayment),
        };
      }),
      catchError(() => of(assigned))
    );
  }

  /** Best label for the person/party a spec reports for, across both flavors. */
  private deriveSpecDisplayName(spec: any): string {
    if (!spec) return '';
    const u = spec.userObject;
    const full = [u?.given_name, u?.family_name].filter(Boolean).join(' ').trim();
    return (u?.friendlyName?.trim())
      || full
      || (u?.email?.trim())
      || (spec.reportingPartyName?.trim())
      || (spec.reporting_party?.trim())
      || (spec.userId?.trim())
      || '';
  }

  /** The reporting party's name, mirroring the spec view's "Party" field. */
  private deriveSpecPartyName(spec: any): string {
    if (!spec) return '';
    return (spec.reportingPartyObject?.name?.trim())
      || (spec.reportingPartyName?.trim())
      || (spec.reporting_party?.trim())
      || '';
  }

  /** Defensive normalize — guarantees all three arrays + a counts object exist. */
  private normalizeAssigned(res: any, userEmail: string): AssignedSubmissions {
    const forReview: AssignedSubmissionRow[] = Array.isArray(res?.forReview) ? res.forReview : [];
    const forApproval: AssignedSubmissionRow[] = Array.isArray(res?.forApproval) ? res.forApproval : [];
    const forPayment: AssignedSubmissionRow[] = Array.isArray(res?.forPayment) ? res.forPayment : [];
    return {
      userEmail: res?.userEmail ?? userEmail,
      counts: {
        forReview: res?.counts?.forReview ?? forReview.length,
        forApproval: res?.counts?.forApproval ?? forApproval.length,
        forPayment: res?.counts?.forPayment ?? forPayment.length
      },
      truncated: res?.truncated === true,
      forReview, forApproval, forPayment
    };
  }

  getAllTimesheetSubmissionsById(contractId: string): Observable<any[]> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_BY_CONTRACT + contractId;
    return this.http.get<any>(url).pipe(
      map(response => response?.submissions || response?.submission_periods || [])
    );
  }

  getTimesheetSubmission(contractId: string, dateOfRecord: string): Observable<any> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_PERIOD + contractId + '/' + dateOfRecord;
    return this.http.get<any>(url).pipe(
      map(response => response?.submission || response)
    );
  }

  getTimesheetDay(contractId: string, date: string): Observable<any> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_DAY + contractId + '/' + date;
    return this.http.get<any>(url);
  }

  logHours(contractId: string, date: string, dayPayload: any): Observable<any> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_LOG_HOURS + contractId + '/' + date;
    return this.http.post(url, dayPayload);
  }

  writePeriod(contractId: string, date: string, periodData: any): Observable<any> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_WRITE_PERIOD + contractId + '/' + date;
    return this.http.post(url, periodData);
  }

  /**
   * LEGACY generic transition — submit/recall (owner actions) stay on this path
   * (the Flutter app submits through it, §7). The back-half review/approve/reject/
   * payment actions move to the discrete /approvals/* methods below on cbhi.
   */
  updateTimesheetSubmissionPeriod(
    submissionId: string,
    status: string,
    changedBy: string,
    reason?: string,
    approverName?: string
  ): Observable<any> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_UPDATE_PERIOD + submissionId;
    const body: any = { status, changedBy };
    if (reason) body.reason = reason;
    // On an approver "approve" (status='approved' while under_review), the backend
    // records this in approvals[] with approverName; harmless on other transitions.
    if (approverName) body.approverName = approverName;
    return this.http.post(url, body);
  }

  // ── Back-half approval actions (cbhi, §7) ──────────────────────────────────
  // Discrete routes; actor is derived from the ID token server-side (NO changedBy
  // in the body). reject/bounce carry a required { note }. Errors are NOT caught
  // here — the queue surfaces 403 (wrong actor), 409 (status conflict / race), and
  // 400 NOTE_REQUIRED. Each route appends the submissionId.

  /** Reviewer approve: submitted → under_review. */
  reviewApproveSubmission(submissionId: string): Observable<any> {
    return this.http.post((environment as any).URL_TIMESHEET_APPROVALS_REVIEW + submissionId, {});
  }

  /** Approver approve: under_review → approved (gated by minApprovals server-side). */
  approveSubmission(submissionId: string): Observable<any> {
    return this.http.post((environment as any).URL_TIMESHEET_APPROVALS_APPROVE + submissionId, {});
  }

  /** Reject (reviewer or approver): → rejected. Note required. */
  rejectSubmission(submissionId: string, note: string): Observable<any> {
    return this.http.post((environment as any).URL_TIMESHEET_APPROVALS_REJECT + submissionId, { note });
  }

  /** Accountant bounce ("start over"): approved/pending_payment → under_review. Note required. */
  bounceSubmission(submissionId: string, note: string): Observable<any> {
    return this.http.post((environment as any).URL_TIMESHEET_APPROVALS_BOUNCE + submissionId, { note });
  }

  /** Accountant: approved → pending_payment. */
  markPendingPayment(submissionId: string): Observable<any> {
    return this.http.post((environment as any).URL_TIMESHEET_APPROVALS_PENDING_PAYMENT + submissionId, {});
  }

  /**
   * Accountant: pending_payment → paid. The check number entered by the payment
   * approver is sent in the body and must be persisted with the submission by
   * the backend (see the "/approvals/paid checkNumber" backend item). Sent as a
   * string to preserve leading zeros / non-numeric check identifiers.
   */
  markPaid(submissionId: string, checkNumber?: string): Observable<any> {
    const body = checkNumber != null && checkNumber !== '' ? { checkNumber } : {};
    return this.http.post((environment as any).URL_TIMESHEET_APPROVALS_PAID + submissionId, body);
  }

  /**
   * Every APPROVED submission in the org — "approved, waiting to be paid."
   *
   * A status list, NOT a work list. For an org that pays outside the app (cbhi →
   * NetSuite) `approved` is terminal and carries no assignee, so these rows do
   * not appear in anyone's assigned queue; this read is what keeps the pipeline
   * visible without putting a person on the hook for work they can't do.
   *
   * Rows leave it when the payment is recorded and the status becomes `paid`.
   *
   * Uses the existing POST /submissions list endpoint (status filter) and reshapes
   * the raw submissions into the queue's row shape, then runs them through the same
   * spec enrichment the assigned queue uses so the cards render identically.
   *
   * Scale note: that endpoint Scans the submissions table and filters in memory.
   * Fine at current volume (cbhi has ~24 submissions total); if this list grows
   * into the thousands it wants a status-keyed index server-side.
   */
  getApprovedAwaitingPayment(limit = 500): Observable<AssignedSubmissionRow[]> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_LIST;
    if (!url) return of([]);
    return this.http.post<any>(url, { status: 'approved', limit }).pipe(
      map(res => {
        const raw: any[] = Array.isArray(res?.submissions) ? res.submissions
          : Array.isArray(res) ? res : [];
        return raw.map(sub => this.toQueueRow(sub));
      }),
      switchMap(rows => this.enrichReportingPartyNames({
        userEmail: '',
        counts: { forReview: 0, forApproval: 0, forPayment: rows.length },
        forReview: [], forApproval: [], forPayment: rows
      })),
      map(enriched => enriched.forPayment),
      catchError(err => {
        console.error('[TimesheetsService] getApprovedAwaitingPayment failed:', err?.status, err?.message);
        return of([] as AssignedSubmissionRow[]);
      })
    );
  }

  /**
   * Reshape a raw submission record into the work-list row shape. The list
   * endpoint returns the stored document, so hours and the period total have to
   * be read out of `totals` (the assigned endpoint denormalizes them at read
   * time). `periodStart`/`periodEnd` aren't stored — the card falls back to
   * `dateOfRecord`.
   */
  private toQueueRow(sub: any): AssignedSubmissionRow {
    const t = sub?.totals ?? {};
    const hours = ['primaryHours', 'supplementalHours', 'premiumHours']
      .reduce((sum, k) => sum + (parseFloat(t[k]) || 0), 0);
    const grand = parseFloat(t.grandTotal ?? sub?.totalAmount);
    return {
      submissionId: sub?.submissionId,
      contractId: sub?.contractId,
      specId: sub?.specId,
      reportingParty: sub?.reportingParty ?? undefined,
      reportingPartyName: sub?.reportingPartyName ?? undefined,
      dateOfRecord: sub?.dateOfRecord,
      frequency: sub?.frequency,
      status: sub?.status,
      totalHours: Math.round(hours * 100) / 100,
      totalAmount: isNaN(grand) ? null : grand,
      approvals: Array.isArray(sub?.approvals) ? sub.approvals : [],
      checkNumber: sub?.checkNumber,
      updatedAt: sub?.updatedAt
    };
  }

  listSubmissions(filters?: { userId?: string; contractId?: string; dateFrom?: string; dateTo?: string }): Observable<any[]> {
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_LIST;
    return this.http.post<any>(url, filters || {}).pipe(
      map(response => response?.submissions || [])
    );
  }

  getTimesheetsInWorkflow(status: string): Observable<any> {
    // This maps to submissions filtered by status — use the list endpoint
    const url = (environment as any).URL_TIMESHEET_SUBMISSIONS_LIST;
    return this.http.post<any>(url, { status });
  }

  // ── Google Sheets delegates ────────────────────────────

  getFixedRateEvents() {
    return firstValueFrom(this.timesheetSheetsService.getFixedRateEvents());
  }

  getAllTimesheetStatuses() {
    return firstValueFrom(this.timesheetSheetsService.getAllTimesheetStatuses());
  }

  getAllTimesheetTypes() {
    return firstValueFrom(this.timesheetSheetsService.getAllTimesheetTypes());
  }

  getAllTimesheetFrequencies() {
    return firstValueFrom(this.timesheetSheetsService.getAllTimesheetFrequencies());
  }

  getAllTimesheetCostCenterCodes() {
    return firstValueFrom(this.timesheetSheetsService.getAllTimesheetCostCenterCodes());
  }

  getAllTimesheetActivities() {
    return firstValueFrom(this.timesheetSheetsService.getAllTimesheetActivities());
  }

  getPbjJobCodes() {
    return firstValueFrom(this.timesheetSheetsService.getPbjJobCodes());
  }

  getCommunicationByColumn(tabName: string, headerLetter: string) {
    return firstValueFrom(this.timesheetSheetsService.getCommunicationByColumn(tabName, headerLetter));
  }
}
